import { useState, useEffect, useMemo, useContext, useCallback } from "react";
import toastError from "../../errors/toastError";
import { format, sub } from "date-fns";
import api from "../../services/api";
import useDebounce from "../useDebounce";
import { AuthContext } from "../../context/Auth/AuthContext";
import socketWorkerInstance from "../../services/SocketWorker";

const useTickets = ({
  searchParam,
  tags,
  users,
  pageNumber,
  status,
  date,
  updatedAt,
  showAll,
  isStarred, // ⭐ Filtrar apenas favoritos
  queueIds,
  withUnreadMessages,
  whatsappIds,
  statusFilter,
  forceSearch,
  userFilter,
  sortTickets,
  searchOnMessages,
  ticketTabId,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const { user, socket } = useContext(AuthContext);
  
  // ✅ Estado para forçar refetch após reconexão (useState é reativo, useRef não é)
  const [forceRefetchCounter, setForceRefetchCounter] = useState(0);

  const debouncedSearchParam = useDebounce(searchParam, 500);

  const parsedQueueIds = useMemo(() => {
    if (Array.isArray(queueIds)) return queueIds.filter(Boolean);
    try {
      const arr = JSON.parse(queueIds || "[]");
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch (_) {
      return [];
    }
  }, [queueIds]);

  // 🔒 Regra de filas alinhada ao backend:
  // - Se queueIds veio vazio, usar todas as filas do usuário logado
  // - allTicket ("SEM FILA") permite incluir APENAS tickets com queueId NULL
  const userQueueIds = useMemo(
    () => (Array.isArray(user?.queues) ? user.queues.map((q) => q.id) : []),
    [user?.queues]
  );

  const effectiveQueueIds = useMemo(() => {
    return parsedQueueIds.length > 0 ? parsedQueueIds : userQueueIds;
  }, [parsedQueueIds, userQueueIds]);

  // ✅ Callback para refetch após reconexão do socket
  // IMPORTANTE: Usar setState para disparar re-render (useRef.current não é reativo)
  const triggerRefetch = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("🔄 [useTickets] Socket reconectado - forçando refetch de tickets...");
    }
    setForceRefetchCounter(prev => prev + 1);
  }, []);

  // ✅ Registrar callback no SocketWorker para sincronização pós-reconexão
  useEffect(() => {
    if (!user?.companyId) return;
    
    // Obter instância do SocketWorker
    const socketWorker = socketWorkerInstance(user.companyId, user.id);
    if (!socketWorker || !socketWorker.onReconnect) return;
    
    const unsubscribe = socketWorker.onReconnect((reason) => {
      if (reason === "reconnected" || reason === "tab-reactivated") {
        triggerRefetch();
      }
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.companyId, user?.id, triggerRefetch]);

  useEffect(() => {
    setTickets([]);
  }, [
    debouncedSearchParam,
    tags,
    users,
    status,
    date,
    updatedAt,
    showAll,
    isStarred,
    queueIds,
    withUnreadMessages,
    whatsappIds,
    statusFilter,
    forceSearch,
    sortTickets,
    searchOnMessages,
    ticketTabId,
    user?.allowGroup,
    user?.allHistoric,
    user?.allTicket,
    user?.allUserChat,
    user?.profile,
  ]);

  useEffect(() => {
    let isMounted = true;
    
    setLoading(true);
    const fetchTickets = async () => {
      if (userFilter === undefined || userFilter === null) {
        try {
          const requestShowAll =
            showAll || (status === "closed" && user?.allowGroup === true);
          const { data } = await api.get("/tickets", {
            params: {
              searchParam: debouncedSearchParam,
              pageNumber,
              tags,
              users,
              status,
              date,
              updatedAt,
              showAll: requestShowAll ? "true" : "false",
              isStarred: isStarred ? "true" : undefined, // ⭐ Filtrar favoritos
              queueIds,
              withUnreadMessages,
              whatsapps: whatsappIds,
              statusFilter,
              sortTickets,
              searchOnMessages,
              ticketTabId: ticketTabId || undefined,
            },
          });

          // ✅ Verificar se ainda está montado antes de atualizar estado
          if (!isMounted) return;

          if (!data || !Array.isArray(data.tickets)) {
            setLoading(false);
            return;
          }

          const postProcess = (arr) => {
            if (status === "closed" && user?.allowGroup === true && !showAll) {
              return arr.filter(
                (t) => t && (t.isGroup === true || t.userId === user?.id)
              );
            }
            return arr;
          };

          setTickets((prevTickets) => {
            if (pageNumber === 1) {
              return postProcess(data.tickets);
            }
            const prevIds = new Set(prevTickets.map((t) => t && t.id));
            const toAppend = data.tickets.filter(
              (t) => t && t.id && !prevIds.has(t.id)
            );
            return postProcess([...prevTickets, ...toAppend]);
          });

          setHasMore(data.hasMore);
          setCount(data.count);
          setLoading(false);
        } catch (err) {
          if (isMounted) {
            setLoading(false);
            toastError(err);
          }
        }
      } else {
        try {
          const { data } = await api.get("/dashboard/moments", {
            params: {
              status,
              showAll,
              queueIds,
              dateStart: format(sub(new Date(), { days: 30 }), "yyyy-MM-dd"),
              dateEnd: format(new Date(), "yyyy-MM-dd"),
              userId: userFilter,
            },
          });

          // ✅ Verificar se ainda está montado antes de atualizar estado
          if (!isMounted) return;

          let filteredTickets = data.filter(
            (item) => item.userId === userFilter
          );
          setTickets(filteredTickets);
          setHasMore(null);
          setLoading(false);
        } catch (err) {
          if (isMounted) {
            setLoading(false);
            toastError(err);
          }
        }
      }
    };
    fetchTickets();
    
    return () => {
      isMounted = false;
    };
  }, [
    debouncedSearchParam,
    tags,
    users,
    pageNumber,
    status,
    date,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    whatsappIds,
    statusFilter,
    forceSearch,
    sortTickets,
    searchOnMessages,
    userFilter,
    isStarred,
    ticketTabId,
    user?.allowGroup,
    user?.id,
    user?.allHistoric,
    user?.allTicket,
    user?.allUserChat,
    user?.profile,
    forceRefetchCounter, // ✅ Trigger refetch após reconexão (useState é reativo)
  ]);

  useEffect(() => {
    if (!user?.companyId) return;
    if (!socket) return;
    
    const companyId = user.companyId;

    const handleTicketUpdate = (data) => {
      if (data.action === "update") {
        setTickets((prevTickets) => {
          const ticketIndex = prevTickets.findIndex(
            (t) => t.id === data.ticket?.id
          );
          if (ticketIndex !== -1) {
            const updatedTickets = [...prevTickets];
            // ✅ Atualizar ticket completo, incluindo tags e outros arrays aninhados
            updatedTickets[ticketIndex] = {
              ...updatedTickets[ticketIndex],
              ...data.ticket,
              // ✅ Garantir que arrays/objetos aninhados sejam atualizados
              tags: data.ticket?.tags || updatedTickets[ticketIndex].tags || [],
              contact: data.ticket?.contact || updatedTickets[ticketIndex].contact || {},
              user: data.ticket?.user || updatedTickets[ticketIndex].user || null,
              queue: data.ticket?.queue || updatedTickets[ticketIndex].queue || null,
              whatsapp: data.ticket?.whatsapp || updatedTickets[ticketIndex].whatsapp || {},
            };
            if (sortTickets === "updatedAt") {
              updatedTickets.sort(
                (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
              );
            }
            return updatedTickets;
          }
          return prevTickets;
        });
      }
      if (data.action === "create") {
        setTickets((prevTickets) => {
          // 🔒 CRITICAL: “Olho fechado” (showAll=false) NÃO pode inserir tickets
          // de outros atendentes via socket.
          // Regra: permitir pending/lgpd (de qualquer um) OU tickets do próprio usuário.
          const ticketStatus = data.ticket?.status;
          const isPendingLike = ticketStatus === "pending" || ticketStatus === "lgpd";
          const isVisibleWithShowAllOff = () => {
            if (showAll) return true;
            // Grupos seguem regra existente do app (não bloquear aqui)
            if (status === "group") return true;
            if (isPendingLike) return true;
            // Fail-closed: se userId não veio, não inserir
            if (data.ticket?.userId === undefined) return false;
            return data.ticket?.userId === user?.id;
          };

          const matchesCurrentStatus = status
            ? data.ticket?.status === status
            : true;
          const matchesQueueIds = (() => {
            const qid = data.ticket?.queueId;
            if (qid == null) {
              // Regra aprovada: SEM FILA só faz sentido para pending/lgpd
              if (!isPendingLike) return false;
              return user?.allTicket === "enabled";
            }
            return effectiveQueueIds.length > 0
              ? effectiveQueueIds.includes(qid)
              : false;
          })();

          if (ticketTabId) {
            const tTabId = data.ticket?.ticketTabId;
            if (tTabId === undefined || !("ticketTabId" in (data.ticket || {}))) return prevTickets;
            if (tTabId != null && tTabId !== ticketTabId) return prevTickets;
          }

          if (matchesCurrentStatus && matchesQueueIds && isVisibleWithShowAllOff()) {
            const newTicketArray = [data.ticket, ...prevTickets];
            if (sortTickets === "updatedAt") {
              newTicketArray.sort(
                (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
              );
            }
            return newTicketArray;
          }
          return prevTickets;
        });
      }
      if (data.action === "delete") {
        setTickets((prevTickets) =>
          prevTickets.filter((ticket) => ticket.id !== data.ticketId)
        );
      }
    };

    const eventName = `company-${companyId}-ticket`;
    socket.on(eventName, handleTicketUpdate);

    return () => {
      // ✅ FIX: Verificar se socket ainda existe no cleanup
      if (socket) {
        socket.off(eventName, handleTicketUpdate);
      }
    };
  }, [user?.companyId, user?.id, user?.allTicket, showAll, sortTickets, status, effectiveQueueIds, ticketTabId, socket]);

  // ✅ PERFORMANCE: Removido useMemo desnecessário (não fazia transformação)
  return { tickets, loading, hasMore, count };
};

export default useTickets;
